import React, { useState, useEffect, useCallback } from 'react';
import { meetingMinutesApi, ataItemsApi } from '../api';
import { toast } from 'sonner';
import { validateDateFields, extractApiError, formatDateBR } from '../utils/dateValidation';
import { NotebookPen, NotebookText, Plus, Edit, Trash2, X, Loader2, Eye, Users, Send, CheckCircle2, Clock, Download } from 'lucide-react';
import AtaItems from './AtaItems';

const FORM_CODE = 'F-022';
const FORM_REVISION = '02';

const EMPTY_PARTICIPANT = { name: '', role: '', signature: '', email: '' };

const EMPTY_FORM = {
  realization_date: '',
  subject: '',
  participants: [{ ...EMPTY_PARTICIPANT }],
  // Pauta and Ações are kept as two parallel arrays of the same length —
  // item i of one is the pair of item i of the other (row i of the table).
  pauta_items: [''],
  action_items: [''],
};

// Pads the shorter of two parallel arrays with '' so every Pauta row has a
// matching Ações row (and vice-versa) — keeps them positionally paired.
const pairArrays = (a, b) => {
  const len = Math.max(a.length, b.length, 1);
  const pad = (arr) => Array.from({ length: len }, (_, i) => arr[i] ?? '');
  return [pad(a), pad(b)];
};

const MeetingMinutes = () => {
  const [activeTab, setActiveTab] = useState('atas');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [ataCatalog, setAtaCatalog] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await meetingMinutesApi.list();
      setRecords(res.data);
    } catch {
      toast.error('Erro ao carregar atas de reunião');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    ataItemsApi.list({ is_active: true }).then((res) => setAtaCatalog(res.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = async (record) => {
    try {
      const res = await meetingMinutesApi.get(record.id);
      const full = res.data;
      setEditing(full);
      const [pauta_items, action_items] = pairArrays(full.pauta_items || [], full.action_items || []);
      setForm({
        realization_date: full.realization_date || '',
        subject: full.subject || '',
        participants: full.participants?.length ? full.participants : [{ ...EMPTY_PARTICIPANT }],
        pauta_items,
        action_items,
      });
      setShowModal(true);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar ata'));
    }
  };

  const openView = async (record) => {
    try {
      const res = await meetingMinutesApi.get(record.id);
      setViewing(res.data);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao carregar ata'));
    }
  };

  const handleRequestSignature = async (idx) => {
    try {
      await meetingMinutesApi.requestSignature(viewing.id, idx);
      toast.success('Convite de assinatura enviado por e-mail');
      const res = await meetingMinutesApi.get(viewing.id);
      setViewing(res.data);
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao enviar convite de assinatura'));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([{ value: form.realization_date, label: 'Realização' }]);
    if (dateError) { toast.error(dateError); return; }

    setSaving(true);
    // Drop only rows where BOTH sides are empty — keeps pauta_items[i] and
    // action_items[i] as the same related row after filtering.
    const keepIdx = form.pauta_items
      .map((_, i) => i)
      .filter((i) => form.pauta_items[i].trim() || form.action_items[i].trim());
    const payload = {
      realization_date: form.realization_date || null,
      subject: form.subject || null,
      participants: form.participants.filter((p) => p.name.trim()),
      pauta_items: keepIdx.map((i) => form.pauta_items[i]),
      action_items: keepIdx.map((i) => form.action_items[i]),
    };
    try {
      if (editing) {
        await meetingMinutesApi.update(editing.id, payload);
        toast.success('Ata atualizada com sucesso');
      } else {
        await meetingMinutesApi.create(payload);
        toast.success('Ata registrada com sucesso');
      }
      setShowModal(false);
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar ata'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async (record) => {
    setDownloadingId(record.id);
    try {
      const res = await meetingMinutesApi.getPdf(record.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    try {
      await meetingMinutesApi.delete(confirmDelete.id);
      toast.success('Ata excluída');
      setConfirmDelete(null);
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao excluir ata'));
    }
  };

  // Participants
  const updateParticipant = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      participants: f.participants.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));
  };
  const addParticipant = () => setForm((f) => ({ ...f, participants: [...f.participants, { ...EMPTY_PARTICIPANT }] }));
  const removeParticipant = (idx) => setForm((f) => ({ ...f, participants: f.participants.filter((_, i) => i !== idx) }));

  // Pauta / Ações — kept as paired rows (pauta_items[i] relates to action_items[i])
  const updateLine = (key, idx, value) => {
    setForm((f) => ({ ...f, [key]: f[key].map((line, i) => (i === idx ? value : line)) }));
  };
  const addRow = () => setForm((f) => ({ ...f, pauta_items: [...f.pauta_items, ''], action_items: [...f.action_items, ''] }));
  const removeRow = (idx) => setForm((f) => ({
    ...f,
    pauta_items: f.pauta_items.filter((_, i) => i !== idx),
    action_items: f.action_items.filter((_, i) => i !== idx),
  }));
  // Fills the first row whose `key` column (pauta_items/action_items) is
  // still empty; if every row already has that column filled, adds a new
  // paired row instead of breaking the pauta<->ação relation.
  const insertFromCatalog = (key, description) => {
    if (!description) return;
    setForm((f) => {
      const emptyIdx = f[key].findIndex((l) => !l.trim());
      if (emptyIdx >= 0) {
        return { ...f, [key]: f[key].map((l, i) => (i === emptyIdx ? description : l)) };
      }
      const pauta_items = key === 'pauta_items' ? [...f.pauta_items, description] : [...f.pauta_items, ''];
      const action_items = key === 'action_items' ? [...f.action_items, description] : [...f.action_items, ''];
      return { ...f, pauta_items, action_items };
    });
  };

  if (activeTab === 'items') {
    return (
      <div className="space-y-6">
        <div className="flex gap-1 border-b border-gray-700 mb-2">
          {[
            { id: 'atas', label: 'Ata de Reunião', icon: NotebookPen },
            { id: 'items', label: 'Itens de ATA', icon: NotebookText },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                activeTab === tab.id ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>
        <AtaItems />
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-700 mb-2">
        {[
          { id: 'atas', label: 'Ata de Reunião', icon: NotebookPen },
          { id: 'items', label: 'Itens de ATA', icon: NotebookText },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              activeTab === tab.id ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <NotebookPen size={28} className="text-blue-400" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Ata de Reunião</h1>
          </div>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-2">
          <Plus size={16} /> Nova Ata
        </button>
      </div>

      {/* List */}
      {records.length === 0 ? (
        <div className="card text-center py-20 text-gray-400">
          <NotebookPen size={48} className="mx-auto mb-3 opacity-30" />
          <p>Nenhuma ata de reunião registrada</p>
          <button onClick={openCreate} className="btn-primary mt-4 mx-auto">Registrar Primeira Ata</button>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Realização</th>
                <th>Referente a</th>
                <th>Participantes</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td className="font-mono text-white">{r.code}</td>
                  <td className="text-gray-400">{formatDateBR(r.realization_date)}</td>
                  <td className="text-white">{r.subject || '-'}</td>
                  <td className="text-gray-400">
                    <span className="flex items-center gap-1">
                      <Users size={14} /> {r.participants_count}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openView(r)} className="p-1 text-gray-400 hover:text-green-400" title="Visualizar">
                        <Eye size={18} />
                      </button>
                      <button onClick={() => openEdit(r)} className="p-1 text-gray-400 hover:text-blue-400" title="Editar">
                        <Edit size={18} />
                      </button>
                      <button onClick={() => handleDownloadPdf(r)} disabled={downloadingId === r.id} className="p-1 text-gray-400 hover:text-purple-400" title="Baixar PDF">
                        {downloadingId === r.id ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                      </button>
                      <button onClick={() => setConfirmDelete(r)} className="p-1 text-gray-400 hover:text-red-400" title="Excluir">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{editing ? 'Editar' : 'Nova'} Ata de Reunião</h2>
                <p className="text-xs text-gray-500">{FORM_CODE} Rev.{FORM_REVISION} — Ata de Reunião</p>
              </div>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Realização</label>
                  <input
                    type="date"
                    value={form.realization_date}
                    onChange={(e) => setForm({ ...form, realization_date: e.target.value })}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Referente a</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="input w-full"
                    placeholder="Assunto da reunião"
                  />
                </div>
              </div>

              {/* Participantes */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Participantes</label>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Nome</th>
                        <th>Função</th>
                        <th>Assinatura</th>
                        <th>E-mail (para assinatura digital)</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.participants.map((p, idx) => (
                        <tr key={idx}>
                          <td>
                            <input type="text" value={p.name} onChange={(e) => updateParticipant(idx, 'name', e.target.value)} className="input w-full" />
                          </td>
                          <td>
                            <input type="text" value={p.role} onChange={(e) => updateParticipant(idx, 'role', e.target.value)} className="input w-full" />
                          </td>
                          <td>
                            <input type="text" value={p.signature} onChange={(e) => updateParticipant(idx, 'signature', e.target.value)} className="input w-full" />
                          </td>
                          <td>
                            <input type="email" value={p.email || ''} onChange={(e) => updateParticipant(idx, 'email', e.target.value)} className="input w-full" placeholder="nome@email.com" />
                          </td>
                          <td>
                            <button type="button" onClick={() => removeParticipant(idx)} className="p-1 text-gray-400 hover:text-red-400" title="Remover">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button type="button" onClick={addParticipant} className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300 mt-2">
                  <Plus size={16} /> Adicionar Participante
                </button>
              </div>

              {/* Pauta / Ações — cada linha relaciona um item de pauta ao seu item de ação correspondente */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Pauta / Ações</label>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr><th>Pauta</th><th>Ação relacionada</th><th></th></tr>
                    </thead>
                    <tbody>
                      {form.pauta_items.map((line, idx) => (
                        <tr key={idx}>
                          <td>
                            <input
                              type="text"
                              value={line}
                              onChange={(e) => updateLine('pauta_items', idx, e.target.value)}
                              className="input w-full"
                              placeholder={`Pauta ${idx + 1}`}
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={form.action_items[idx] ?? ''}
                              onChange={(e) => updateLine('action_items', idx, e.target.value)}
                              className="input w-full"
                              placeholder={`Ação ${idx + 1}`}
                            />
                          </td>
                          <td>
                            <button type="button" onClick={() => removeRow(idx)} className="p-1 text-gray-400 hover:text-red-400" title="Remover linha">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <button type="button" onClick={addRow} className="flex items-center gap-1 text-sm text-green-400 hover:text-green-300">
                    <Plus size={16} /> Adicionar Linha
                  </button>
                  {ataCatalog.some((i) => i.category === 'pauta') && (
                    <select
                      value=""
                      onChange={(e) => insertFromCatalog('pauta_items', e.target.value)}
                      className="input py-1 text-sm w-56"
                      title="Inserir item de pauta cadastrado"
                    >
                      <option value="">+ Pauta do catálogo...</option>
                      {ataCatalog.filter((i) => i.category === 'pauta').map((i) => (
                        <option key={i.id} value={i.description}>{i.code} — {i.description}</option>
                      ))}
                    </select>
                  )}
                  {ataCatalog.some((i) => i.category === 'acao') && (
                    <select
                      value=""
                      onChange={(e) => insertFromCatalog('action_items', e.target.value)}
                      className="input py-1 text-sm w-56"
                      title="Inserir ação cadastrada"
                    >
                      <option value="">+ Ação do catálogo...</option>
                      {ataCatalog.filter((i) => i.category === 'acao').map((i) => (
                        <option key={i.id} value={i.description}>{i.code} — {i.description}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary" disabled={saving}>Cancelar</button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* View Modal */}
      {viewing && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">{FORM_CODE} — Ata de Reunião</h2>
                <p className="text-xs text-gray-500">Cód.: {FORM_CODE} · Rev.: {FORM_REVISION} · {viewing.code}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownloadPdf(viewing)}
                  disabled={downloadingId === viewing.id}
                  className="btn-secondary text-sm flex items-center gap-2"
                >
                  {downloadingId === viewing.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  Baixar PDF
                </button>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-white">
                  <X size={24} />
                </button>
              </div>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Realização</p>
                  <p className="text-white font-medium">{formatDateBR(viewing.realization_date)}</p>
                </div>
                <div>
                  <p className="text-gray-400">Referente a</p>
                  <p className="text-white font-medium">{viewing.subject || '-'}</p>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-300 mb-2">Participantes</p>
                {viewing.participants?.length ? (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr><th>Nome</th><th>Função</th><th>Assinatura Digital</th><th></th></tr>
                      </thead>
                      <tbody>
                        {viewing.participants.map((p, idx) => (
                          <tr key={idx}>
                            <td className="text-white">{p.name}</td>
                            <td className="text-gray-400">{p.role || '-'}</td>
                            <td>
                              {p.signed ? (
                                <div className="flex items-center gap-2">
                                  <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                                    <CheckCircle2 size={12} /> Assinado {p.signed_at ? new Date(p.signed_at).toLocaleString('pt-BR') : ''}
                                  </span>
                                  {p.signature_image && (
                                    <img src={p.signature_image} alt="Assinatura" style={{ height: '28px', background: '#fff', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.1)' }} />
                                  )}
                                </div>
                              ) : p.email ? (
                                <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">
                                  <Clock size={12} /> Pendente
                                </span>
                              ) : (
                                <span className="text-xs text-gray-500">Sem e-mail cadastrado</span>
                              )}
                            </td>
                            <td>
                              {!p.signed && p.email && (
                                <button
                                  onClick={() => handleRequestSignature(idx)}
                                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                  title="Enviar convite de assinatura por e-mail"
                                >
                                  <Send size={14} /> Enviar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-gray-500">Nenhum participante registrado</p>}
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-300 mb-2">Pauta / Ações</p>
                {viewing.pauta_items?.length || viewing.action_items?.length ? (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr><th>Pauta</th><th>Ação relacionada</th></tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: Math.max(viewing.pauta_items?.length || 0, viewing.action_items?.length || 0) }).map((_, idx) => (
                          <tr key={idx}>
                            <td className="text-gray-300">{viewing.pauta_items?.[idx] || '-'}</td>
                            <td className="text-gray-300">{viewing.action_items?.[idx] || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <p className="text-sm text-gray-500">Nenhum item de pauta/ação registrado</p>}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="card p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-500/20">
                <Trash2 size={22} className="text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Confirmar Exclusão</h3>
            </div>
            <p className="text-gray-400 text-sm">
              Tem certeza que deseja excluir a ata <span className="text-white font-medium">{confirmDelete.code}</span>?
              Esta ação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="btn-secondary">Voltar</button>
              <button
                onClick={handleConfirmDelete}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                <Trash2 size={16} /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingMinutes;
